import type { FC } from "react";
import { Link } from "react-router";
import type { IconType } from "react-icons";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Flex,
  HStack,
  Icon,
  Text,
  chakra,
  type ButtonProps,
} from "@chakra-ui/react";

export interface MenuItemButtonProps {
  label: string;
  icon: IconType;
  id?: string;
  to?: string;
  active?: boolean;
  onClick?: () => void;
  colorPalette?: string;
  buttonProps?: ButtonProps;
  notificationText?: string | number;
}

const getActiveStyleProps = (active: boolean, colorPalette: string) => ({
  bg: active ? `${colorPalette}.100` : "transparent",
  _hover: { bg: `${colorPalette}.100` },
  _dark: {
    _hover: { bg: `${colorPalette}.900` },
    bg: active ? `${colorPalette}.800` : "transparent",
  },
});

const ChakraLink = chakra(Link);

export const MenuItemButton: FC<MenuItemButtonProps> = ({
  id,
  to,
  icon,
  label,
  onClick,
  buttonProps,
  active = false,
  notificationText,
  colorPalette = "teal",
}) => {
  const button = (
    <Button
      py={0}
      pl={0}
      id={id}
      w="full"
      gap={2}
      size="xs"
      flexDir="row"
      display="flex"
      variant="plain"
      onClick={onClick}
      colorPalette={colorPalette}
      justifyContent="flex-start"
      {...getActiveStyleProps(active, colorPalette)}
      {...buttonProps}
    >
      <HStack
        w="full"
        alignItems="center"
        overflow="hidden"
        justifyContent="flex-start"
      >
        <Flex
          py={4}
          px={2}
          h="fit-content"
          position="relative"
          alignItems="center"
          justifyContent="center"
        >
          <Badge
            top={2}
            right={0}
            size="xs"
            fontSize="8px"
            w="fit-content"
            variant="solid"
            colorPalette="red"
            position="absolute"
            display={notificationText ? void 0 : "none"}
          >
            {notificationText}
          </Badge>
          <Icon as={icon} w="1.1rem" p={0} />
        </Flex>
        <Text truncate fontSize="sm">
          {label}
        </Text>
      </HStack>
    </Button>
  );

  if (!to) return button;

  return (
    <ChakraLink to={to} w="full">
      {button}
    </ChakraLink>
  );
};
